import React from 'react';
import { Link } from 'react-router-dom';
import { currencyOnFileNote, fmtDateShort } from '../../utils/format';
import { formatCurrency, formatDate, formatReadable } from './contractDetailUtils';

// Countdown chip + meter fill share the ExpirationBar / ObligationsCard
// thresholds: red ≤ 14d or overdue, yellow ≤ 60d, green beyond.
export function termChip(days) {
  if (days == null) return null;
  if (days < 0) return { cls: 'chip sq red', text: `${Math.abs(days)}d overdue` };
  if (days === 0) return { cls: 'chip sq red', text: 'expires today' };
  if (days <= 14) return { cls: 'chip sq red', text: `${days}d left` };
  if (days <= 60) return { cls: 'chip sq yellow', text: `${days}d left` };
  return { cls: 'chip sq green', text: `${days}d left` };
}

function meterFill(days) {
  if (days != null && days <= 14) return 'var(--color-red-500)';
  if (days != null && days <= 60) return 'var(--color-yellow-500)';
  return 'var(--color-green-500)';
}

// `May 1 → Oct 31, 2026` — the start date drops its year when it matches
// the end date's, so the range reads as one phrase.
function termRange(effective, expiration) {
  const sameYear = String(effective).slice(0, 4) === String(expiration).slice(0, 4);
  const start = sameYear ? fmtDateShort(effective) : formatDate(effective);
  return `${start} → ${formatDate(expiration)}`;
}

// Empty-cell affordance: an em dash plus (for writers) an Add link into the
// owning editor — the strip doubles as the inventory of what's missing.
function EmptyFact({ canWrite, to, onClick, title }) {
  return (
    <div className="fact-value">
      <span className="tertiary">—</span>
      {canWrite && (to ? (
        <Link to={to} className="btn ghost xs" title={title}>Add</Link>
      ) : (
        <button type="button" className="btn ghost xs" onClick={onClick} title={title}>Add</button>
      ))}
    </div>
  );
}

// The four questions legal asks first, in one card above the tabs: when does
// it run out, what's it worth, how does it renew, whose law governs. Dates,
// money, and law render here and nowhere else on the page.
export default function ContractFactStrip({
  canWriteContracts,
  contract,
  contractId,
  daysToExp,
  onOpenLawEditor,
  termPct,
}) {
  const c = contract;
  const chip = termChip(daysToExp);
  const hasValue = typeof c.contractValue === 'number';
  const editHref = (hash) => `/contracts/${contractId}/edit${hash}`;

  // UNRESOLVABLE CURRENCY (doc-204): contracts.currency is free-form, and some
  // spellings are deliberately left unmapped because guessing would be wrong —
  // a bare '$' is USD/CAD/AUD/MXN/SGD (see format.js CURRENCY_ALIASES). Those
  // rows print a bare number here, and the Currency field itself sits a TAB
  // away in Key Clauses › Commercial, so this cell names what is on file
  // instead of leaving a naked amount. Wording matches the Counterparty 360
  // tile (CounterpartySections.valueSub) so both surfaces say it identically.
  // A blank/missing currency is the USD default, NOT unresolved (displayCode).
  // currencyOnFileNote owns the trim + length clamp so this surface and the
  // Counterparty 360 tile cannot drift (doc-221) — the raw string used to be
  // interpolated here verbatim, which wrapped this cell to three lines and grew
  // every cell in the equal-height grid.
  const currencyNote = currencyOnFileNote(c.currency);
  // One sub-line, not two — the currency note leads because it qualifies the
  // number directly above it.
  const valueSub = [
    currencyNote && currencyNote.text,
    c.paymentTerms && c.paymentTerms !== 'none' ? formatReadable(c.paymentTerms) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div className="card fact-strip">
      <div className="fact-cell">
        <div className="fact-label">Term</div>
        {c.expirationDate ? (
          <>
            <div className="fact-value">
              <span className="mono" style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.effectiveDate
                  ? termRange(c.effectiveDate, c.expirationDate)
                  : `→ ${formatDate(c.expirationDate)}`}
              </span>
              {chip && <span className={chip.cls}>{chip.text}</span>}
            </div>
            {c.effectiveDate && (
              <div className="fact-meter">
                <span style={{ width: `${Math.round(termPct * 100)}%`, background: meterFill(daysToExp) }} />
              </div>
            )}
          </>
        ) : c.effectiveDate ? (
          <div className="fact-value" style={{ fontSize: 13 }}>Evergreen</div>
        ) : (
          <EmptyFact canWrite={canWriteContracts} to={editHref('#sec-dates')} title="Add term dates" />
        )}
      </div>

      <div className="fact-cell">
        <div className="fact-label">Value</div>
        {hasValue ? (
          <>
            <div className="fact-value">
              <span className="mono" style={{ fontSize: 13 }}>{formatCurrency(c.contractValue, c.currency)}</span>
            </div>
            {valueSub && (
              // The title only exists when the note was clamped, so hovering
              // still recovers the full stored string (doc-221).
              <div className="fact-sub" title={(currencyNote && currencyNote.title) || undefined}>{valueSub}</div>
            )}
          </>
        ) : (
          <EmptyFact canWrite={canWriteContracts} to={editHref('#sec-financial')} title="Add contract value" />
        )}
      </div>

      <div className="fact-cell">
        <div className="fact-label">Renewal</div>
        {c.renewalType ? (
          <>
            <div className="fact-value" style={{ fontSize: 13 }}>{formatReadable(c.renewalType)}</div>
            {c.autoRenewalNoticeDate ? (
              <div className="fact-sub mono">Notice by {formatDate(c.autoRenewalNoticeDate)}</div>
            ) : c.renewalNoticeDays ? (
              <div className="fact-sub">{c.renewalNoticeDays}d renewal notice</div>
            ) : null}
          </>
        ) : (
          <EmptyFact canWrite={canWriteContracts} to={editHref('#sec-dates')} title="Add renewal terms" />
        )}
      </div>

      <div className="fact-cell">
        <div className="fact-label">Governing law</div>
        {c.governingLaw ? (
          <div className="fact-value" style={{ fontSize: 13, whiteSpace: 'normal' }}>{c.governingLaw}</div>
        ) : (
          // Law already has an in-page editor (Key Clauses › Legal Venue), so
          // this opens it instead of growing a duplicate form field.
          <EmptyFact
            canWrite={canWriteContracts}
            onClick={onOpenLawEditor}
            title="Set governing law (Key Clauses › Legal Venue)"
          />
        )}
      </div>
    </div>
  );
}
